import { getUnavailableVehicleDataV2 } from "api/vehicles/vehicle";
import { useData } from "data/data-provider";
import moment from "moment";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { changeDate } from "utils/changeDate";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomDay from "../DatePickerCustomDay";

// Types
import {
  DatePickerType,
  RentalCarDatesRangeInputProps,
  UnavailabilityData,
} from "./types/rentalCarDatesRangeInput.types";

// Utils
import {
  createDateWithoutTimezone,
  formatDate,
  formatTime,
  parsePartialUnavailability,
} from "./utils/rentalCarDatesRangeInput.utils";
import {
  getAllWeeksForSelection,
  getSelectedWeeksDateRange,
  getWeeksInMonth,
  weekContainsUnavailableDates,
} from "./utils/weeksHelper";

// Hooks
import { useClickOutside } from "./hooks/useClickOutside";
import { useDateAvailability } from "./hooks/useDateAvailability";
import { useDialInteraction } from "./hooks/useDialInteraction";
import { useScreenSize } from "./hooks/useScreenSize";

// Components
import DatesView from "./components/DatesView";
import MonthsView from "./components/MonthsView";
import TabSwitcher from "./components/TabSwitcher";
import WeeksView from "./components/WeeksView";
import RentalCarTimesRangeInput from "./RentalCarTimesRangeInput";
import { XMarkIcon } from "@heroicons/react/24/outline";

const StartDateTimeRange: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  isMiddle,
  bookingDetails,
}) => {
  const {
    ourCompany,
    startDate,
    endDate,
    startTime,
    endTime,
    s_Time,
    e_Time,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    partiallyUnavailableTimes,
    setLongTermRentalDuration,
    pickupLocation,
    selectedWeeks,
    setSelectedMonth,
    setSelectedWeeks,
    selectedMonth,
    monthStartDate,
    monthEndDate,
    setMonthEndDate,
    setMonthStartDate,
    setLongTermRentalNature,
    activeTab,
    setActiveTab,
    weeksViewMonth,
  } = useData();

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    [],
  );
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const [showDatePicker, setShowDatePicker] = useState<DatePickerType>(null);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(null);
  
  // Temporary state for date selection in dates tab
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  // Refs for circular dial
  const dialRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // Set activeTab based on bookingDetails.long_term_rental_nature
  useEffect(() => {
    if (bookingDetails?.long_term_rental_nature === "monthly") {
      setActiveTab("months");
    } else if (bookingDetails?.long_term_rental_nature === "weekly") {
      setActiveTab("weeks");
    } else if (bookingDetails && !bookingDetails.is_long_term_rental) {
      setActiveTab("dates");
    }
  }, [bookingDetails?.long_term_rental_nature, bookingDetails?.is_long_term_rental]);

  // Initialize dates and times from bookingDetails
  useEffect(() => {
    if (bookingDetails?.pick_up_time && bookingDetails?.drop_off_time) {
      const pickUpDateTime = new Date(bookingDetails.pick_up_time);
      const dropOffDateTime = new Date(bookingDetails.drop_off_time);
      
      // Set dates
      setStartDate(pickUpDateTime);
      setEndDate(dropOffDateTime);
      
      // Set times
      const pickUpTime = moment(pickUpDateTime).format('HH:mm');
      const dropOffTime = moment(dropOffDateTime).format('HH:mm');
      setStartTime(pickUpTime);
      setEndTime(dropOffTime);
      
      // For monthly rentals, set month dates
      if (bookingDetails.long_term_rental_nature === "monthly") {
        setMonthStartDate(pickUpDateTime);
        setMonthEndDate(dropOffDateTime);
        
        // Calculate months
        const start = moment(pickUpDateTime);
        const end = moment(dropOffDateTime);
        const monthsDiff = end.diff(start, 'months', true);
        const calculatedMonths = Math.max(1, Math.ceil(monthsDiff));
        setSelectedMonth(calculatedMonths);
      }
      
      // For weekly rentals, calculate weeks
      if (bookingDetails.long_term_rental_nature === "weekly") {
        const weeks: string[] = [];
        let current = moment(pickUpDateTime);
        const end = moment(dropOffDateTime);
        
        while (current.isSameOrBefore(end, 'day')) {
          weeks.push(current.format('YYYY-MM-DD'));
          current.add(7, 'days');
        }
        
        if (weeks.length > 0) {
          setSelectedWeeks(weeks);
        }
      }
    }
  }, [bookingDetails?.pick_up_time, bookingDetails?.drop_off_time, bookingDetails?.long_term_rental_nature]);

  // Custom hooks
  const { isLargeScreen } = useScreenSize();
  const monthsToShow = isLargeScreen ? 2 : 1;

  const { isDragging, handleMouseDown, handleTouchStart } = useDialInteraction(
    dialRef,
    setSelectedMonth,
  );

  const { isDateSelectable, isRangeAvailable, isPartialOverlapValid } =
    useDateAvailability(fullyUnavailableDates, partialUnavailability);

  useClickOutside(panelRef, isPanelOpen && !showDatePicker, () => setIsPanelOpen(false));

  // Initialize temp dates when panel opens or when activeTab is "dates"
  useEffect(() => {
    if (isPanelOpen && activeTab === "dates") {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [isPanelOpen, activeTab]);

  useEffect(() => {
    const newEndDate = moment(monthStartDate)
      .add(selectedMonth * 30 - 1, "days")
      .toDate();
    setMonthEndDate(newEndDate);
  }, [selectedMonth, monthStartDate]);

  const calculateMonthsFromDates = useCallback((start: Date, end: Date) => {
    const days = moment(end).diff(moment(start), "days") + 1;
    const months = Math.round(days / 30);
    return Math.max(1, Math.min(12, months));
  }, []);

  const openDatePickerModal = (type: DatePickerType) => {
    setShowDatePicker(type);
    setTempSelectedDate(type === "start" ? monthStartDate : monthEndDate);
  };

  const handleModalDateChange = (date: Date | null) => {
    if (date) {
      setTempSelectedDate(date);
    }
  };

  const handleModalSave = () => {
    if (!tempSelectedDate || !showDatePicker) return;

    if (showDatePicker === "start") {
      setMonthStartDate(tempSelectedDate);
      const months = calculateMonthsFromDates(tempSelectedDate, monthEndDate);
      setSelectedMonth(months);
    } else if (showDatePicker === "end") {
      setMonthEndDate(tempSelectedDate);
      const months = calculateMonthsFromDates(monthStartDate, tempSelectedDate);
      setSelectedMonth(months);
    }
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  const handleModalClose = () => {
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  const fetchUnavailableDates = async (startDate: Date, endDate: Date) => {
    if (!selectedCar?.id) {
      console.warn("No car selected, unable to fetch unavailable dates.");
      return;
    }

    setIsLoading(true);
    try {
      const startDateString = new Date(startDate);
      startDateString.setMonth(startDateString.getMonth() - 1);
      const endDateString = new Date(endDate);
      endDateString.setMonth(endDateString.getMonth() + 1);

      const startDateStr = formatDate(startDateString);
      const endDateStr = formatDate(endDateString);

      const response = await getUnavailableVehicleDataV2(
        {
          start_date: startDateStr,
          end_date: endDateStr,
          booking_to_exclude: bookingDetails?.id,
        },
        selectedCar.id,
        pickupLocation?.id,
      );

      if (response.data) {
        const unavailableData = response.data as UnavailabilityData;
        const fullyUnavailable = unavailableData.full_unavailable.map(
          (dateStr) => createDateWithoutTimezone(dateStr),
        );
        setFullyUnavailableDates(fullyUnavailable);
        const partialUnavailable = parsePartialUnavailability(
          unavailableData.partial_unavailable,
        );
        setPartialUnavailability(partialUnavailable);
        if (setPartiallyUnavailableTimes) {
          setPartiallyUnavailableTimes(partialUnavailable as any);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast.error("Failed to load availability data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const startMoment = moment(dates[0]);
    const endMoment = moment(dates[1]);
    const isCheck = isRangeAvailable(startMoment, endMoment);
    
    // Use default time if time is empty
    const validStartTime =
      startTime && startTime.trim() !== "" ? startTime : s_Time;
    const validEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    if (!isCheck) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error("Selected range is fully unavailable.");
      return;
    }

    const isPartialValid = isPartialOverlapValid(startMoment, endMoment);
    if (!isPartialValid) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error(
        "Selected range contains a partially unavailable date in the middle.",
      );
      return;
    }
    
    // Update temporary state only
    setTempStartDate(dates[0]);
    setTempEndDate(dates[1]);

    const today = new Date();
    const visibleEndDate = new Date(today);
    visibleEndDate.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEndDate);
  };

  useEffect(() => {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  // Fetch unavailable dates when weeks view month changes
  useEffect(() => {
    if (activeTab === 'weeks' && weeksViewMonth) {
      const monthStart = moment(weeksViewMonth).startOf('month').toDate();
      const monthEnd = moment(weeksViewMonth).endOf('month').toDate();
      fetchUnavailableDates(monthStart, monthEnd);
    }
  }, [weeksViewMonth, activeTab]);

  // Auto-sync date interval when selectedWeeks or times change
  useEffect(() => {
    if (activeTab === 'weeks' && selectedWeeks && selectedWeeks.length > 0 && startTime && endTime) {
      const allWeeks = getAllWeeksForSelection(selectedWeeks, fullyUnavailableDates);
      const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, startTime, endTime);
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
      }
    }
  }, [selectedWeeks, startTime, endTime, fullyUnavailableDates, activeTab]);

  // Adjust times when availability data is loaded and times are blocked
  useEffect(() => {
    if (!startDate || !endDate || Object.keys(partialUnavailability).length === 0) return;
    
    const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
    
    // Check if current times are blocked for the selected dates
    const startTimeBlocked = isTimeBlocked(startDate, currentStartTime);
    const endTimeBlocked = isTimeBlocked(endDate, currentEndTime);
    
    if (startTimeBlocked || endTimeBlocked) {
      const validStartTime = startTimeBlocked ? getAvailableTime(startDate, s_Time) : currentStartTime;
      const validEndTime = endTimeBlocked ? getAvailableTime(endDate, e_Time) : currentEndTime;
      
      // Update times if they changed
      if (validStartTime !== startTime) {
        setStartTime(validStartTime);
      }
      if (validEndTime !== endTime) {
        setEndTime(validEndTime);
      }
      
      // Update the date objects with the new times
      if (validStartTime !== currentStartTime || validEndTime !== currentEndTime) {
        changeDate([startDate, endDate], setStartDate, setEndDate, validStartTime, validEndTime);
      }
    }
  }, [partialUnavailability, startDate, endDate]);

  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0,
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
  };

  const handleMonthSave = () => {
    const startMoment = moment(monthStartDate);
    const endMoment = moment(monthEndDate);

    if (isRangeAvailable(startMoment, endMoment)) {
      // Check if this is first selection (no dates were previously selected)
      const isFirstSelection = !startDate && !endDate;
      const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
      const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
      
      // On first selection, always validate. On subsequent selections, only adjust if blocked
      const shouldCheckStartTime = isFirstSelection || isTimeBlocked(monthStartDate, defaultStartTime);
      const shouldCheckEndTime = isFirstSelection || isTimeBlocked(monthEndDate, defaultEndTime);
      
      const validStartTime = (monthStartDate && shouldCheckStartTime)
        ? getAvailableTime(monthStartDate, s_Time)
        : defaultStartTime;
      const validEndTime = (monthEndDate && shouldCheckEndTime)
        ? getAvailableTime(monthEndDate, e_Time)
        : defaultEndTime;
      
      // Update the state with adjusted times only if they actually need to change
      if (validStartTime !== startTime) {
        setStartTime(validStartTime);
      }
      if (validEndTime !== endTime) {
        setEndTime(validEndTime);
      }
      
      changeDate(
        [monthStartDate, monthEndDate],
        setStartDate,
        setEndDate,
        validStartTime,
        validEndTime,
      );
      const totalDays = endMoment.diff(startMoment, "days") + 1;
      const totalMonths = Math.round(totalDays / 30);
      setLongTermRentalDuration(totalMonths);
    } else {
      toast.error("Selected period contains unavailable dates.");
    }
  };

  const handleWeeksSave = () => {
    const allWeeks = getAllWeeksForSelection(selectedWeeks, fullyUnavailableDates);
    const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, startTime, endTime);
    if (!range) {
      toast.error("Please select at least one week.");
      return;
    }
    const sortedWeeks = [...selectedWeeks].sort();
    const firstWeekKey = sortedWeeks[0];

    const weeksWithUnavailability = allWeeks.filter((week, index) => {
      const keyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
      const key = moment(keyDate).format("YYYY-MM-DD");
      const isFirstWeek = key === firstWeekKey;

      return (
        selectedWeeks.includes(key) &&
        weekContainsUnavailableDates(
          week,
          fullyUnavailableDates,
          partialUnavailability,
          isFirstWeek,
        )
      );
    });

    if (weeksWithUnavailability.length > 0) {
      toast.error("One or more selected weeks contain unavailable dates.");
      return;
    }

    const startMoment = moment(range.start);
    const endMoment = moment(range.end);

    if (isRangeAvailable(startMoment, endMoment)) {
      // Check if this is first selection (no dates were previously selected)
      const isFirstSelection = !startDate && !endDate;
      const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
      const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
      
      // On first selection, always validate. On subsequent selections, only adjust if blocked
      const shouldCheckStartTime = isFirstSelection || isTimeBlocked(range.start, defaultStartTime);
      const shouldCheckEndTime = isFirstSelection || isTimeBlocked(range.end, defaultEndTime);
      
      const validStartTime = (range.start && shouldCheckStartTime)
        ? getAvailableTime(range.start, s_Time)
        : defaultStartTime;
      const validEndTime = (range.end && shouldCheckEndTime)
        ? getAvailableTime(range.end, e_Time)
        : defaultEndTime;
      
      // Update the state with adjusted times only if they actually need to change
      if (validStartTime !== startTime) {
        setStartTime(validStartTime);
      }
      if (validEndTime !== endTime) {
        setEndTime(validEndTime);
      }
      
      changeDate(
        [range.start, range.end],
        setStartDate,
        setEndDate,
        validStartTime,
        validEndTime,
      );
      setLongTermRentalDuration(selectedWeeks.length);
    } else {
      toast.error("Selected weeks contain unavailable dates.");
    }
  };

  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
  ): boolean => {
    if (!date) return false;
    // Use local partialUnavailability state for immediate accuracy
    const unavailabilityData = partialUnavailability || partiallyUnavailableTimes || {};
    const dateKey = moment(date).format("YYYY-MM-DD");
    const ranges = unavailabilityData[dateKey];
    if (!Array.isArray(ranges) || ranges.length === 0) return false;

    const timeMoment = moment(time, "HH:mm");
    return ranges.some(({ from, to }: { from: string; to: string }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Helper function to find next available time if default is unavailable
  const getAvailableTime = (
    date: Date | null | undefined,
    defaultTime: string,
  ): string => {
    if (!date) return defaultTime;
    
    // If default time is available, use it
    if (!isTimeBlocked(date, defaultTime)) {
      return defaultTime;
    }

    // Find first available time slot
    const timeSlots = Array.from({ length: 24 }, (_, h) => [
      `${h.toString().padStart(2, "0")}:00`,
      `${h.toString().padStart(2, "0")}:30`,
    ]).flat();

    for (const time of timeSlots) {
      if (!isTimeBlocked(date, time)) {
        return time;
      }
    }

    // If all times are blocked, return default (shouldn't happen)
    return defaultTime;
  };

  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    const dateStr = formatDate(date);
    const isPartial = !!partialUnavailability[dateStr];

    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
      />
    );
  };

  useEffect(() => {
    if (isPanelOpen && window.innerWidth < 768) {
      setTimeout(() => {
        if (panelRef.current) {
          const topPosition = panelRef.current.getBoundingClientRect().top;
          const offset =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetScrollPosition = topPosition + offset - 120;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [isPanelOpen]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "dates":
        return (
          <DatesView
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
      case "weeks":
        return (
          <WeeksView
            unavailableDates={fullyUnavailableDates}
            partialUnavailability={partialUnavailability}
          />
        );
      case "months":
        return (
          <MonthsView
            dialRef={dialRef}
            isDragging={isDragging}
            handleMouseDown={handleMouseDown}
            handleTouchStart={handleTouchStart}
            openDatePickerModal={openDatePickerModal}
            showDatePicker={showDatePicker}
            tempSelectedDate={tempSelectedDate}
            handleModalDateChange={handleModalDateChange}
            handleModalSave={handleModalSave}
            handleModalClose={handleModalClose}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            startDate={startDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
            isLoading={isLoading}
          />
        );
      default:
        return (
          <DatesView
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
    }
  };

  const handleSave = () => {
    const validStartTime =
      startTime && startTime.trim() !== "" ? startTime : s_Time;
    const validEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    switch (activeTab) {
      case "weeks":
        handleWeeksSave();
        setLongTermRentalNature("weekly");
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
      case "months":
        handleMonthSave();
        setLongTermRentalNature("monthly");
        setSelectedWeeks([]);
        break;
      case "dates":
      default:
        // Apply temporary dates when saving
        if (tempStartDate && tempEndDate) {
          const startMoment = moment(tempStartDate);
          const endMoment = moment(tempEndDate);
          
          // Check if this is first selection (no dates were previously selected)
          const isFirstSelection = !startDate && !endDate;
          const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
          const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
          
          // On first selection, always validate. On subsequent selections, only adjust if blocked
          const shouldCheckStartTime = isFirstSelection || isTimeBlocked(tempStartDate, defaultStartTime);
          const shouldCheckEndTime = isFirstSelection || isTimeBlocked(tempEndDate, defaultEndTime);
          
          const validStartTime = (tempStartDate && shouldCheckStartTime) 
            ? getAvailableTime(tempStartDate, s_Time) 
            : defaultStartTime;
          const validEndTime = (tempEndDate && shouldCheckEndTime)
            ? getAvailableTime(tempEndDate, e_Time)
            : defaultEndTime;
          
          // Update the state with adjusted times only if they actually need to change
          if (validStartTime !== startTime) {
            setStartTime(validStartTime);
          }
          if (validEndTime !== endTime) {
            setEndTime(validEndTime);
          }
          
          changeDate([tempStartDate, tempEndDate], setStartDate, setEndDate, validStartTime, validEndTime);
          const totalDaysExclusive = endMoment.diff(startMoment, "days") + 1;
          if (totalDaysExclusive) {
            setLongTermRentalDuration(totalDaysExclusive);
          }
        } else if (!startDate || !endDate) {
          toast.error("Please select start and end dates.");
          return;
        }
        setLongTermRentalNature("daily");
        setSelectedWeeks([]);
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
    }
    setIsPanelOpen(false);
  };

  const use24Hour = ourCompany?.default_time_format === "24_hour";

  const isStartDateLocked = useMemo(() => {
    if (!bookingDetails?.pick_up_time) return false;
    
    const pickUpDateTime = new Date(bookingDetails.pick_up_time);
    const currentDateTime = new Date();
    const pickUpDateOnly = new Date(pickUpDateTime);
    pickUpDateOnly.setHours(0, 0, 0, 0);
    const currentDateOnly = new Date(currentDateTime);
    currentDateOnly.setHours(0, 0, 0, 0);
    
    return pickUpDateOnly < currentDateOnly || 
           (pickUpDateOnly.getTime() === currentDateOnly.getTime() && pickUpDateTime < currentDateTime);
  }, [bookingDetails?.pick_up_time]);

  return (
    <>
      <div className={`RentalCarDatesRangeInput relative flex ${className}`}>
        <div className={`flex-1 flex items-center focus:outline-none`}>
          <div
            className={`flex-1 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none`}
          >
            <div className="w-full text-left">
              <p className="text-[#1F2937] font-semibold">From</p>
              <div className="xl:text-lg font-medium text-text-color grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`cursor-pointer text-left relative focus:outline-none transition-colors ${
                    isStartDateLocked 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:text-primary-200'
                  }`}
                  onClick={() => {
                    if (isStartDateLocked) {
                      toast.error("The pickup date is locked because it's in the past.");
                      return;
                    }
                    setIsPanelOpen((open) => !open);
                  }}
                  disabled={isStartDateLocked}
                >
                  {startDate?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                  }) || "Date"}
                  {isStartDateLocked && (
                    <span className="ml-1 text-xs">🔒</span>
                  )}
                  {startDate && !isStartDateLocked && (
                    <ClearDataButton
                      onClick={() => {
                        setStartDate(null);
                        setPartiallyAvailableStartDate(null);
                        setSelectedWeeks([]);
                        setSelectedMonth(0);
                        setMonthStartDate(new Date());
                        setMonthEndDate(moment().add(29, "days").toDate());
                      }}
                    />
                  )}
                </button>

                <RentalCarTimesRangeInput
                  className="w-full"
                  showTimeType="start"
                  hasButtonSubmit={false}
                  isLocked={isStartDateLocked}
                />
              </div>
            </div>
          </div>
        </div>

        {isPanelOpen && (
          <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1"></div>
        )}

        {isPanelOpen && (
          <div
            ref={panelRef}
            className={`fixed left-1/2 z-[9999] mt-3 top-20 w-screen max-w-96 -translate-x-1/2 ${
              isMiddle ? "lg:-translate-x-1/3" : "-translate-x-1/2"
            } xl:-translate-x-1/2 transform px-4 sm:px-0 md:max-w-2xl`}
            tabIndex={-1}
            onScroll={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 bg-white p-8">
              {!bookingDetails && <TabSwitcher />}

              {isLoading ? (
                <div className="pointer-events-none opacity-60 select-none">
                  <div className="relative">{renderTabContent()}</div>
                  <div className="absolute left-0 bottom-0 w-full px-8">
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                      <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 pointer-events-auto" />
                </div>
              ) : (
                renderTabContent()
              )}

              <div className="flex justify-between items-center align-center mt-6">
                <div>
                  {selectedWeeks.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedWeeks([]);
                      }}
                      className=" w-[120px] text-red-600 hover:text-red-700 hover:bg-red-100 bg-red-50 font-semibold rounded-lg py-3 transition-all space-x-2"
                    >
                      <span>Clear Selection</span>
                    </button>
                  )}
                </div>
                <div>
                  <button
                    className=" w-[120px] bg-primary-200 hover:bg-primary-700 text-button-color font-semibold rounded-lg py-3 transition-all space-x-2"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSave();
                    }}
                  >
                    <span>Save Dates</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StartDateTimeRange;
